import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { saveAs } from 'file-saver';
import { Customer } from '../../classes/customer';
import { CustomerService } from '../../services/customer';
import { Request } from '../../classes/request';
import { RequestService } from '../../services/request';
import { Document } from '../../classes/document';
import { DocumentService } from '../../services/document';
import { FileService } from '../../services/file';
import { AuthService } from '../../services/auth';
import { User } from '../../classes/user';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GeneralModalComponent } from '../../modals/general/general-modal/general-modal.component';
import {
    RequestCompleteModalComponent
} from '../../modals/request/request-complete-modal/request-complete-modal.component';
import {
    RequestUncompleteModalComponent
} from '../../modals/request/request-uncomplete-modal/request-uncomplete-modal.component';
import { RequestDeleteModalComponent } from '../../modals/request/request-delete-modal/request-delete-modal.component';
import { EmailService } from '../../services/email';
import { mergeCustomerDocumentsIntoList } from '../../utils/customerDocuments';
import { customerEmailAddresses } from '../../utils/customerEmails';
import { openDocumentPreview } from '../../modals/document/document-preview-modal/document-preview-modal.component';

@Component({
    selector: 'app-customer-detail',
    templateUrl: './customer-detail.component.html',
    styleUrls: ['./customer-detail.component.css']
})
export class CustomerDetailComponent implements OnInit {
    user: User = new User();
    customer: Customer = new Customer();
    linkedRequests: Array<Request> = [];
    documents: Array<Document> = [];
    documentTypes: any = {
        1: 'Visura camerale',
        2: 'Ultimo Bilancio approvato',
        3: 'Marca da bollo da 16 euro (foto o scanner)',
        4: 'Carta di identità del titolare della società',
        5: 'Offerta del fornitore non timbrata',
        6: 'Contratto + Privacy + Mandato professionale + Procura firmati digitalmente',
        7: 'Distinta di pagamento',
        8: 'DURC',
        9: 'Registri IVA',
        10: 'Fatture, DDT, Verbali di consegna',
        11: 'Perizia 4.0',
        12: 'Carta di identità del perito 4.0',
        13: 'Certificazione ex ante 5.0',
        14: 'Carta di identità dell\'ingegnere energetico',
        15: 'Libro cespiti',
        16: 'Visura catastale della struttura produttiva',
        17: 'Contratto di affitto della struttura produttiva',
        18: 'Documento sicurezza lavoro',
        19: 'Assicurazione professionale ingegnere 4.0',
        20: 'Fattura ingegnere 4.0',
        21: 'Assicurazione professionale ingegnere energetico',
        22: 'Fattura ingegnere energetico',
        23: 'Modulo di certificazione Esco/Ege',
        24: 'Dichiarazione di terzietà del valutatore indipendente',
        25: 'Carta di identità del firmatario della Certificazione contabile',
        26: 'Certificazione contabile',
        27: 'Fattura di acquisto del bene',
        28: 'Contratto di leasing',
        29: 'DVR'
    };

    constructor(private router: Router,
                private route: ActivatedRoute,
                private authService: AuthService,
                private modalService: NgbModal,
                private customerService: CustomerService,
                private requestService: RequestService,
                private documentService: DocumentService,
                private fileService: FileService,
                private emailService: EmailService) {
    }

    ngOnInit(): void {
        this.authService.isUserSignedIn()
            .subscribe({
                next: () => {
                    this.authService.getUser()
                        .subscribe({
                            next: (user: any) => {
                                this.user = user;
                                if (this.user.FKIDUserRole !== 1) {
                                    this.router.navigate(['/dashboard']);
                                    return;
                                }
                                this.loadCustomer();
                            },
                            error: () => {
                                this.router.navigate(['/login']);
                            }
                        });
                },
                error: () => {
                    this.router.navigate(['/login']);
                }
            });
    }

    private loadCustomer(): void {
        this.route.paramMap.subscribe(params => {
            this.customerService.getByID(params.get('id'))
                .subscribe({
                    next: (customer: Customer) => {
                        this.customer = customer;
                        this.loadDocuments();
                        this.loadLinkedRequests();
                    },
                    error: (err: HttpErrorResponse) => {
                        console.log(err);
                    }
                });
        });
    }

    private loadDocuments(): void {
        if (!this.customer.IDCustomer) {
            this.documents = [];
            return;
        }
        this.documentService.getAllByIDCustomer(this.customer.IDCustomer.toString())
            .subscribe({
                next: (documents: any) => {
                    const byType: { [key: number]: Document[] } = {};
                    mergeCustomerDocumentsIntoList(byType, documents);
                    this.documents = [];
                    Object.keys(byType).forEach((key) => {
                        this.documents.push(...byType[Number(key)]);
                    });
                },
                error: (err: HttpErrorResponse) => {
                    console.log(err);
                }
            });
    }

    private loadLinkedRequests(): void {
        if (!this.customer.IDCustomer) {
            this.linkedRequests = [];
            return;
        }
        this.requestService.getByIDCustomer(this.customer.IDCustomer.toString())
            .subscribe({
                next: (requests: Array<Request>) => {
                    this.linkedRequests = requests;
                },
                error: (err: HttpErrorResponse) => {
                    console.log(err);
                }
            });
    }

    goToRequestDetail(idRequest: number | undefined) {
        this.router.navigate(['/richiesta/' + idRequest]);
    }

    copyRequestLink(requestToken: string) {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(window.location.origin + '/richiesta?session=' + requestToken).then(() => {
                console.log('Copiato!');
            }).catch(err => {
                console.error('Errore nella copia', err);
            });
        } else {
            console.warn('Clipboard API non supportata');
        }
    }

    openCompleteRequestModal(request: Request) {
        const modalRef = this.modalService.open(RequestCompleteModalComponent);
        modalRef.componentInstance.request = request;
        modalRef.result.then((requestBack: Request) => {
            if (requestBack) {
                requestBack.ProgressStatus = 10;
                this.requestService.update(requestBack)
                    .subscribe({
                        next: () => {},
                        error: (err: HttpErrorResponse) => {
                            console.log(err);
                        }
                    });
            }
        }, () => {});
    }

    openUncompleteRequestModal(request: Request) {
        const modalRef = this.modalService.open(RequestUncompleteModalComponent);
        modalRef.componentInstance.request = request;
        modalRef.result.then((requestBack: Request) => {
            if (requestBack) {
                requestBack.ProgressStatus = 9;
                this.requestService.update(requestBack)
                    .subscribe({
                        next: () => {},
                        error: (err: HttpErrorResponse) => {
                            console.log(err);
                        }
                    });
            }
        }, () => {});
    }

    openDeleteRequestModal(request: Request) {
        const modalRef = this.modalService.open(RequestDeleteModalComponent);
        modalRef.componentInstance.request = request;
        modalRef.result.then((requestBack: Request) => {
            if (requestBack) {
                this.requestService.delete(requestBack.IDRequest)
                    .subscribe({
                        next: () => {
                            this.linkedRequests = this.linkedRequests.filter(
                                (currentRequest: Request) => currentRequest.IDRequest !== requestBack.IDRequest
                            );
                        },
                        error: (err: HttpErrorResponse) => {
                            console.log(err);
                        }
                    });
            }
        }, () => {});
    }

    openSendReminderModal(request: Request) {
        const modalRef = this.modalService.open(GeneralModalComponent);
        modalRef.componentInstance.title = 'Invio Sollecito';
        modalRef.componentInstance.text = 'Sei sicuro di voler inviare un sollecito?';
        modalRef.result.then(() => {
            this.emailService.sendReminder(request.IDRequest!)
                .subscribe({
                    error: (err: HttpErrorResponse) => {
                        console.log(err);
                    }
                });
        }, () => {});
    }

    openCompleteDnsModal(request: Request) {
        const modalRef = this.modalService.open(GeneralModalComponent);
        modalRef.componentInstance.title = 'Completamento DNS';
        modalRef.componentInstance.text = 'Confermi il completamento DNS per la pratica "' + (this.customer.CompanyName || request.customer?.CompanyName || '') + '"?';
        modalRef.result.then(() => {
            request.DNSCompleted = true;
            this.requestService.update(request)
                .subscribe({
                    next: () => {},
                    error: (err: HttpErrorResponse) => {
                        request.DNSCompleted = false;
                        console.log(err);
                    }
                });
        }, () => {});
    }

    openCompleteIper4Modal(request: Request) {
        const modalRef = this.modalService.open(GeneralModalComponent);
        modalRef.componentInstance.title = 'Completamento Iperammortamento 4.0';
        modalRef.componentInstance.text = 'Confermi il completamento Iperammortamento 4.0 per la pratica "' + (this.customer.CompanyName || request.customer?.CompanyName || '') + '"?';
        modalRef.result.then(() => {
            request.Iper4Completed = true;
            this.requestService.update(request)
                .subscribe({
                    next: () => {},
                    error: (err: HttpErrorResponse) => {
                        request.Iper4Completed = false;
                        console.log(err);
                    }
                });
        }, () => {});
    }

    openUncompleteDnsModal(request: Request) {
        const modalRef = this.modalService.open(GeneralModalComponent);
        modalRef.componentInstance.title = 'Annullamento completamento DNS';
        modalRef.componentInstance.text = 'Confermi di annullare il completamento DNS per la pratica "' + (this.customer.CompanyName || request.customer?.CompanyName || '') + '"?';
        modalRef.result.then(() => {
            request.DNSCompleted = false;
            this.requestService.update(request)
                .subscribe({
                    next: () => {},
                    error: (err: HttpErrorResponse) => {
                        request.DNSCompleted = true;
                        console.log(err);
                    }
                });
        }, () => {});
    }

    openUncompleteIper4Modal(request: Request) {
        const modalRef = this.modalService.open(GeneralModalComponent);
        modalRef.componentInstance.title = 'Annullamento completamento Iperammortamento 4.0';
        modalRef.componentInstance.text = 'Confermi di annullare il completamento Iperammortamento 4.0 per la pratica "' + (this.customer.CompanyName || request.customer?.CompanyName || '') + '"?';
        modalRef.result.then(() => {
            request.Iper4Completed = false;
            this.requestService.update(request)
                .subscribe({
                    next: () => {},
                    error: (err: HttpErrorResponse) => {
                        request.Iper4Completed = true;
                        console.log(err);
                    }
                });
        }, () => {});
    }

    downloadDocument(document: Document) {
        this.customerDocumentFile(document)
            .subscribe({
                next: (blobFile) => {
                    saveAs(blobFile, document.Path);
                },
                error: (err: HttpErrorResponse) => {
                    console.log(err);
                }
            });
    }

    previewDocument(document: Document) {
        openDocumentPreview(this.modalService, document.Path, () => this.customerDocumentFile(document));
    }

    private customerDocumentFile(document: Document) {
        return this.fileService.download(
            null,
            document.FKIDDocumentType!.toString(),
            document.Path,
            this.customer.IDCustomer!.toString()
        );
    }

    getDocumentTypeLabel(idDocumentType: number | undefined): string {
        if (idDocumentType == null) {
            return '-';
        }
        return this.documentTypes[idDocumentType] || ('Tipologia ' + idDocumentType);
    }

    get customerAddressDisplay(): string {
        const address = this.customer.Address?.trim() || '';
        const houseNumber = this.customer.HouseNumber?.trim() || '';
        const parts = [address, houseNumber].filter(Boolean);
        return parts.length ? parts.join(' ') : '-';
    }

    get emailList(): string[] {
        return customerEmailAddresses(this.customer);
    }

    displayValue(value: string | number | null | undefined): string {
        if (value === null || value === undefined) {
            return '-';
        }
        const trimmed = String(value).trim();
        return trimmed.length ? trimmed : '-';
    }

    displayPlace(place?: string | null, province?: string | null): string {
        const normalizedPlace = place?.trim() || '';
        const normalizedProvince = province?.trim() || '';
        if (!normalizedPlace && !normalizedProvince) {
            return '-';
        }
        if (!normalizedPlace) {
            return `(${normalizedProvince})`;
        }
        return normalizedProvince
            ? `${normalizedPlace} (${normalizedProvince})`
            : normalizedPlace;
    }

    openToggleEnabledModal() {
        const willEnable = !this.customer.Enabled;
        const modalRef = this.modalService.open(GeneralModalComponent);
        modalRef.componentInstance.title = willEnable ? 'Riattivazione Cliente' : 'Disabilitazione Cliente';
        modalRef.componentInstance.text = willEnable
            ? `Confermi la riattivazione del cliente "${this.customer.CompanyName}"?`
            : `Confermi la disabilitazione del cliente "${this.customer.CompanyName}"? Il cliente non verrà eliminato, ma solo nascosto dalle operazioni correnti.`;
        modalRef.result.then(() => {
            this.customer.Enabled = willEnable;
            this.customerService.update(this.customer)
                .subscribe({
                    next: () => {},
                    error: (err: HttpErrorResponse) => {
                        this.customer.Enabled = !willEnable;
                        console.log(err);
                    }
                });
        }, () => {});
    }

}
